
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { MarkdownRenderer } from '@/components/markdown/MarkdownRenderer';
import { TableOfContents } from '@/components/tutorials/TableOfContents';
import { ChapterPagination } from '@/components/tutorials/ChapterPagination';
import { ReadingProgress } from '@/components/tutorials/ReadingProgress';
import { Button } from '@/components/ui/button';
import { useTutorialMetadata } from '@/hooks/useMetadata';
import { getTutorialById } from '@/utils/sample-data';
import { loadChapterContent } from '@/utils/markdown-loader';
import { parseMarkdown } from '@/utils/markdown';

export default function ChapterPage() {
  const { tutorialId, chapterSlug } = useParams<{ tutorialId: string; chapterSlug: string }>();
  const navigate = useNavigate();
  const [chapterContent, setChapterContent] = useState<string>('# Loading...');
  const [contentIsLoading, setContentIsLoading] = useState(true);
  
  const { data: tutorial, isLoading, error } = useTutorialMetadata(tutorialId);
  
  // Fall back to static data if needed
  const fallbackTutorial = !tutorial && tutorialId ? getTutorialById(tutorialId) : null;
  const currentTutorial = tutorial || fallbackTutorial;
  
  useEffect(() => {
    async function fetchChapterContent() {
      if (tutorialId && chapterSlug) {
        setContentIsLoading(true);
        const content = await loadChapterContent(tutorialId, chapterSlug);
        setChapterContent(content);
        setContentIsLoading(false);
      }
    }
    
    fetchChapterContent();
  }, [tutorialId, chapterSlug]);

  // Update document metadata for SEO
  useEffect(() => {
    if (currentTutorial && chapterSlug) {
      const chapter = currentTutorial.chapters.find(c => c.slug === chapterSlug);
      if (chapter) {
        // Update page title
        document.title = `${chapter.title} | ${currentTutorial.title} | Mate4Tech Knowledge Hub`;
        
        // Update meta description
        let metaDescription = document.querySelector('meta[name="description"]');
        if (!metaDescription) {
          metaDescription = document.createElement('meta');
          metaDescription.setAttribute('name', 'description');
          document.head.appendChild(metaDescription);
        }
        metaDescription.setAttribute('content', 
          `Learn about ${chapter.title} in this comprehensive ${currentTutorial.title} tutorial. Part of the Mate4Tech Knowledge Hub.`
        );
        
        // Update Open Graph tags
        let ogTitle = document.querySelector('meta[property="og:title"]');
        if (!ogTitle) {
          ogTitle = document.createElement('meta');
          ogTitle.setAttribute('property', 'og:title');
          document.head.appendChild(ogTitle);
        }
        ogTitle.setAttribute('content', `${chapter.title} | ${currentTutorial.title}`);
        
        let ogDesc = document.querySelector('meta[property="og:description"]');
        if (!ogDesc) {
          ogDesc = document.createElement('meta');
          ogDesc.setAttribute('property', 'og:description');
          document.head.appendChild(ogDesc);
        }
        ogDesc.setAttribute('content', 
          `Learn about ${chapter.title} in this comprehensive ${currentTutorial.title} tutorial.`
        );
        
        let ogImage = document.querySelector('meta[property="og:image"]');
        if (!ogImage) {
          ogImage = document.createElement('meta');
          ogImage.setAttribute('property', 'og:image');
          document.head.appendChild(ogImage);
        }
        ogImage.setAttribute('content', currentTutorial.featuredImage);
        
        // Add canonical URL
        let canonicalLink = document.querySelector('link[rel="canonical"]');
        if (!canonicalLink) {
          canonicalLink = document.createElement('link');
          canonicalLink.setAttribute('rel', 'canonical');
          document.head.appendChild(canonicalLink);
        }
        canonicalLink.setAttribute(
          'href', 
          `https://knowledge.mate4tech.com.au/tutorials/${tutorialId}/chapters/${chapterSlug}`
        );
      }
    }
    
    return () => {
      // Reset title when component unmounts
      document.title = 'Mate4Tech Knowledge Hub';
    };
  }, [currentTutorial, chapterSlug, tutorialId]);

  if (isLoading) {
    return (
      <MainLayout>
        <div className="container py-16 px-4 text-center">
          <div className="animate-pulse">Loading tutorial...</div>
        </div>
      </MainLayout>
    );
  }

  if (error || !currentTutorial) {
    navigate('/tutorials');
    return null;
  }

  const chapter = currentTutorial.chapters.find(c => c.slug === chapterSlug);
  
  if (!chapter) {
    navigate(`/tutorials/${tutorialId}`);
    return null;
  }

  return (
    <MainLayout>
      <ReadingProgress />
      
      <div className="container py-16 px-4">
        <div className="flex flex-col lg:flex-row">
          {/* Main content */}
          <div className="flex-1 lg:px-6">
            <div className="max-w-3xl mx-auto">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate(`/tutorials/${tutorialId}`)}
                className="mb-4 -ml-2 text-muted-foreground"
              >
                ← Back to tutorial
              </Button>
              
              <h1 className="text-3xl md:text-4xl font-bold mb-8">{chapter.title}</h1>
              
              {/* Add structured data for SEO */}
              <script type="application/ld+json" dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "Article",
                  "headline": chapter.title,
                  "description": `Learn about ${chapter.title} in this comprehensive ${currentTutorial.title} tutorial.`,
                  "image": currentTutorial.featuredImage,
                  "author": {
                    "@type": "Organization",
                    "name": currentTutorial.author
                  },
                  "publisher": {
                    "@type": "Organization",
                    "name": "Mate4Tech",
                    "logo": {
                      "@type": "ImageObject",
                      "url": "https://knowledge.mate4tech.com.au/lovable-uploads/de3d0bc8-0f94-4be8-9cb4-e571559a9a53.png"
                    }
                  },
                  "datePublished": currentTutorial.lastUpdated,
                  "dateModified": currentTutorial.lastUpdated
                })
              }} />
              
              <div className="prose dark:prose-invert">
                {contentIsLoading ? (
                  <div className="flex items-center justify-center py-10">
                    <div className="animate-pulse">Loading content...</div>
                  </div>
                ) : (
                  <MarkdownRenderer content={chapterContent} />
                )}
              </div>
              
              <ChapterPagination 
                tutorial={currentTutorial}
                currentChapterSlug={chapterSlug || ''}
              />
            </div>
          </div>
          
          {/* Side content - Table of contents */}
          <div className="lg:w-64 mt-8 lg:mt-0">
            <TableOfContents markdownContent={chapterContent} />
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
